import type { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { type FocusEvent } from 'react'

import type { LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'
import { DateTimeSection } from '../components/DateTimeSection'
import { OpportunityPickerSection } from '../components/OpportunityPickerSection'

import { Loader } from '@percent/lemonade'
import type { OpportunitySelfParticipationDto } from '@percent/workplace-giving/api/opportunity/dto'
import { NoOpportunitiesFound } from './NoOpportunitiesFound'

export type LogOnPlatformActivityFormProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  touched: FormikTouched<LogVolunteeringActivityFormFields>
  errors: FormikErrors<LogVolunteeringActivityFormFields>
  handleBlur: <A = HTMLInputElement>(e: FocusEvent<A>) => void
  formikInstance: FormikProps<LogVolunteeringActivityFormFields>
  allowMultipleEntries: boolean
  duplicateDates?: string[]
  showWorkingHoursCheck: boolean
  workingHoursRequired: boolean
  allowOpportunityChange: boolean
  registeredOpportunitiesCount?: number
  isFetchingRegisteredOpportunities: boolean
  isAwaitingFetchingRegisteredOpportunities: boolean
  fetchingRegisteredOpportunitiesError: boolean
  registeredOpportunitiesQuery: string
  proposedRegisteredOpportunities?: OpportunitySelfParticipationDto[]
  registeredOpportunitiesData?: OpportunitySelfParticipationDto[]
  setRegisteredOpportunitiesQuery: (query: string) => void
  selectedOpportunity?: OpportunitySelfParticipationDto['opportunity']
  setSelectedOpportunity: (opportunity: OpportunitySelfParticipationDto['opportunity']) => void
  isLoading: boolean
  showOverlapWarning?: boolean
  showRewardPreview?: boolean
}>

export function LogOnPlatformActivityForm({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  formikInstance,
  allowMultipleEntries,
  duplicateDates,
  showWorkingHoursCheck,
  workingHoursRequired,
  allowOpportunityChange,
  registeredOpportunitiesCount,
  isFetchingRegisteredOpportunities,
  isAwaitingFetchingRegisteredOpportunities,
  fetchingRegisteredOpportunitiesError,
  registeredOpportunitiesQuery,
  proposedRegisteredOpportunities,
  registeredOpportunitiesData,
  setRegisteredOpportunitiesQuery,
  selectedOpportunity,
  setSelectedOpportunity,
  isLoading,
  showOverlapWarning,
  showRewardPreview
}: LogOnPlatformActivityFormProps) {
  if (isLoading) {
    return <Loader />
  }

  if (!registeredOpportunitiesCount && allowOpportunityChange) {
    return <NoOpportunitiesFound />
  }

  const handleOpportunitySelect = (opportunity: OpportunitySelfParticipationDto['opportunity']) => {
    setFieldValue('opportunityId', opportunity.id)
    setFieldTouched('opportunityId')
    setSelectedOpportunity(opportunity)
  }

  return (
    <>
      <OpportunityPickerSection
        selectedOpportunity={selectedOpportunity}
        proposedOpportunities={proposedRegisteredOpportunities}
        onSelect={handleOpportunitySelect}
        allowOpportunityChange={allowOpportunityChange}
        registeredOpportunitiesData={registeredOpportunitiesData}
        registeredOpportunitiesQuery={registeredOpportunitiesQuery}
        setRegisteredOpportunitiesQuery={setRegisteredOpportunitiesQuery}
        isFetchingRegisteredOpportunities={isFetchingRegisteredOpportunities}
        isAwaitingFetchingRegisteredOpportunities={isAwaitingFetchingRegisteredOpportunities}
        fetchingRegisteredOpportunitiesError={fetchingRegisteredOpportunitiesError}
      />
      <DateTimeSection
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        handleBlur={handleBlur}
        formikInstance={formikInstance}
        allowMultipleEntries={allowMultipleEntries}
        duplicateDates={duplicateDates}
        showWorkingHoursCheck={showWorkingHoursCheck}
        workingHoursRequired={workingHoursRequired}
        showOverlapWarning={showOverlapWarning}
        showRewardPreview={showRewardPreview}
      />
    </>
  )
}
